import { createDebugLogger } from '../utils/debug';
const debug = createDebugLogger('legacyAdpx');
function normalizeLegacyConfig(cfg, callback) {
    if (!cfg.accountId?.trim() && !cfg.awinAdvertiserId?.trim()) {
        throw new Error('[Adpx] init() requires accountId or awinAdvertiserId');
    }
    const w = window;
    const adpxUser = w.AdpxUser ?? {};
    const user = {
        ...adpxUser,
        ...(cfg.attributes ?? {}),
        ...(cfg.subid !== undefined ? { subid: cfg.subid } : {}),
    };
    const opts = {
        accountId: cfg.accountId ?? '',
        user,
    };
    if (cfg.awinAdvertiserId !== undefined)
        opts.awinAdvertiserId = cfg.awinAdvertiserId;
    if (cfg.campaignId !== undefined)
        opts.campaignId = cfg.campaignId;
    // Legacy parity: the old bundle always sent offerId as a number, defaulting to 0
    // (`parseInt(x.offerId ?? 0)`), so it is set unconditionally here too. The API's
    // filter treats 0 as falsy and skips filtering; the literal string "null" (what
    // String(null) produced) was instead adopted as a campaign id and matched nothing.
    opts.offerId = parseInt(String(cfg.offerId ?? 0), 10);
    if (cfg.autoShow !== undefined)
        opts.autoShow = cfg.autoShow;
    if (cfg.autoLoad !== undefined)
        opts.autoLoad = cfg.autoLoad;
    if (cfg.prefetch !== undefined)
        opts.prefetch = cfg.prefetch;
    if (cfg.dev !== undefined)
        opts.dev = cfg.dev;
    if (cfg.theme_id !== undefined)
        opts.theme_id = cfg.theme_id;
    if (cfg.settings !== undefined)
        opts.settings = cfg.settings;
    if (cfg.styles !== undefined)
        opts.styles = cfg.styles;
    if (callback !== undefined)
        opts.userCallback = callback;
    return opts;
}
export function bootstrapLegacyAdpx(ms) {
    const w = window;
    if (w.Adpx)
        return;
    let _inst;
    let _autoDetected = false;
    const guard = (method) => {
        if (!_inst)
            throw new Error(`[Adpx] ${method}() called before init()`);
    };
    const adpxBase = {
        init(config, callback) {
            if (_inst) {
                if (!_autoDetected) {
                    debug.warn('init() called more than once — ignoring.');
                }
                _autoDetected = false;
                return;
            }
            _autoDetected = false;
            _inst = ms.init(normalizeLegacyConfig(config, callback), 'default');
        },
        show() { guard('show'); _inst.show(); },
        showModal() { guard('showModal'); _inst.show(); },
        closeModal() { guard('closeModal'); void _inst.closeModal(); },
        reload() { guard('reload'); void _inst.reload(); },
        refresh() { guard('refresh'); void _inst.refresh(); },
        setApiResponse(r) { guard('setApiResponse'); _inst.setApiResponse(r); },
        getPayload() { guard('getPayload'); return _inst.getPayload(); },
        setPayload(updates) { guard('setPayload'); return _inst.setPayload(updates); },
        dispatchUSPEvent() { guard('dispatchUSPEvent'); _inst.dispatchUSPEvent(); },
        completeSession() { guard('completeSession'); _inst.completeSession(); },
        addSelectedOffers(id) { guard('addSelectedOffers'); _inst.addSelectedOffer(id); },
        removeSelectedOffers(id) { guard('removeSelectedOffers'); _inst.removeSelectedOffer(id); },
    };
    Object.defineProperty(adpxBase, 'sess_id', {
        get: () => _inst?.currentSessionId ?? null,
        enumerable: true,
        configurable: true,
    });
    // Delegate mouState and reused_session as live proxies to the SDK instance.
    Object.defineProperty(adpxBase, 'mouState', {
        get: () => _inst?.mouState ?? { selectedOffers: [] },
        set: (v) => {
            if (_inst)
                _inst.mouState = v;
        },
        enumerable: true,
        configurable: true,
    });
    Object.defineProperty(adpxBase, 'reused_session', {
        get: () => _inst?.reused_session ?? false,
        set: (v) => {
            if (_inst)
                _inst.reused_session = v;
        },
        enumerable: true,
        configurable: true,
    });
    const adpx = adpxBase;
    w.Adpx = adpx;
    if (w.AdpxConfig) {
        setTimeout(() => {
            if (!_inst) {
                _autoDetected = true;
                try {
                    adpx.init(w.AdpxConfig);
                }
                catch (error) {
                    _autoDetected = false;
                    debug.warn('auto-init failed.', error);
                }
            }
        }, 0);
    }
}
